import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Disclosure, DisclosureProps, Transition } from "@headlessui/react";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

// See: https://mui.com/material-ui/api/collapse/
// See: https://headlessui.com/react/disclosure
// See: https://headlessui.com/react/disclosure#transitions

const CollapseVariant = {
  solid: "solid",
  text: "text",
} as const;

const CollapseColor = {
  gray100: "gray100",
  gray200: "gray200",
  primary500: "primary500",
  primary950: "primary950",
} as const;

const CollapseSize = {
  small: "small",
  medium: "medium",
  large: "large",
} as const;

const CollapseSizeConfig = {
  [CollapseVariant.solid]: {
    [CollapseSize.small]: {
      button: "px-4 py-2 text-sm",
      panel: "p-4",
    },
    [CollapseSize.medium]: {
      button: "px-5 py-3 text-md",
      panel: "p-5",
    },
    [CollapseSize.large]: {
      button: "px-6 py-4 text-lg",
      panel: "p-6",
    },
  },
  [CollapseVariant.text]: {
    [CollapseSize.small]: {
      button: "px-0 py-2 text-sm",
      panel: "px-0 py-3",
    },
    [CollapseSize.medium]: {
      button: "px-0 py-3 text-sm",
      panel: "px-0 py-4",
    },
    [CollapseSize.large]: {
      button: "px-0 py-4 text-sm",
      panel: "px-0 py-5",
    },
  },
};

const CollapseColorConfig = {
  [CollapseVariant.solid]: {
    [CollapseColor.gray100]: {
      button: "bg-vpGray-100 border border-vpGray-300 hover:bg-vpGray-200",
      panel: "bg-vpGray-100 border border-t-0 border-vpGray-300",
    },
    [CollapseColor.gray200]: {
      button: "bg-vpGray-200 border border-vpGray-400 hover:bg-vpGray-300",
      panel: "bg-vpGray-200 border border-t-0 border-vpGray-400",
    },
    [CollapseColor.primary500]: {
      button: "bg-vpBlue-500 border border-vpBlue-700 hover:bg-vpBlue-900 text-white",
      panel: "bg-vpBlue-500 border border-t-0 border-vpBlue-700 text-white",
    },
    [CollapseColor.primary950]: {
      button: "bg-vpBlue-950 border border-vpBlue-1000 hover:bg-vpBlue-1000 text-white",
      panel: "bg-vpBlue-950 border border-t-0 border-vpBlue-1000 text-white",
    },
  },
  [CollapseVariant.text]: {
    [CollapseColor.gray100]: {
      button: "bg-white hover:text-vpGray-100",
      panel: "bg-white",
    },
    [CollapseColor.gray200]: {
      button: "bg-white hover:text-vpGray-200",
      panel: "bg-white",
    },
    [CollapseColor.primary500]: {
      button: "bg-white hover:text-vpBlue-500",
      panel: "bg-white",
    },
    [CollapseColor.primary950]: {
      button: "bg-white hover:text-vpBlue-950",
      panel: "bg-white",
    },
  },
} as const;

type CollapsePropsOmits = "onChange";

export interface CollapseProps extends Omit<DisclosureProps<"div">, CollapsePropsOmits> {
  className?: string;
  color?: keyof typeof CollapseColor;
  disabled?: boolean;
  disableGutters?: boolean;
  onChange?: (options: { open: boolean; event: React.MouseEvent<HTMLButtonElement> }) => void;
  open?: boolean;
  size?: keyof typeof CollapseSize;
  slots?: {
    root?: {
      css?: string;
    };
    button?: {
      css?: string;
    };
    panel?: {
      css?: string;
    };
  };
  title?: string;
  variant?: keyof typeof CollapseVariant;
}

function Collapse({
  children,
  className,
  color = "gray100",
  defaultOpen,
  disabled,
  disableGutters,
  onChange,
  open: openProp,
  size = "medium",
  slots,
  title,
  variant = "solid",
  ...props
}: CollapseProps) {
  const [open, setOpen] = useState(openProp || defaultOpen);

  useEffect(() => {
    const isControlledState = openProp !== undefined;

    if (isControlledState) {
      setOpen(openProp);
    }
  }, [openProp]);

  return (
    <Disclosure className={twMerge(classNames(slots?.root?.css, className))} {...props}>
      <div>
        <Disclosure.Button
          className={twMerge(
            classNames(
              "flex w-full items-center justify-between gap-4 font-bold",
              CollapseColorConfig[variant][color].button,
              CollapseSizeConfig[variant][size].button,
              open ? "rounded-t-lg" : "rounded-lg",
              {
                "px-0": disableGutters,
                "opacity-50": disabled,
              },
              slots?.button?.css
            )
          )}
          disabled={disabled}
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            const isUncontrolledState = openProp === undefined;

            const nextOpen = !open;

            if (isUncontrolledState) {
              setOpen(nextOpen);
            }

            onChange?.({ event, open: nextOpen });
          }}
        >
          {title}
          <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} />
        </Disclosure.Button>
        <Transition show={open}>
          <Disclosure.Panel
            className={twMerge(
              classNames(
                "flex flex-col gap-4",
                CollapseColorConfig[variant][color].panel,
                CollapseSizeConfig[variant][size].panel,
                {
                  "rounded-b-lg": open,
                  "px-0": disableGutters,
                },
                slots?.panel?.css
              )
            )}
            static
          >
            {children}
          </Disclosure.Panel>
        </Transition>
      </div>
    </Disclosure>
  );
}

Collapse.displayName = Collapse.name;

export default Collapse;
